import { useEffect, useMemo, useState } from 'react';
import { roomInfo } from '../api';
import { type ServerClock, updateClock } from '../clock';
import { sessionToken } from '../identity';
import { type RoomAuth, connectRoom } from '../socket';
import { useRoom } from '../useRoom';
import { useVerification } from '../useVerification';
import { TableView } from './TableView';
import { Toasts } from './Toasts';

const REFUSAL_MESSAGES: Record<string, string> = {
  bad_token: 'Your identity token was rejected. Clear this site\'s storage and reload.',
  room_full: 'This table has reached its connection limit. Try again in a moment.',
  too_many_connections: 'You already have too many tabs open on this table. Close one and reload.',
};

export function TablePage({ roomId, connect = connectRoom }: { roomId: string; connect?: typeof connectRoom }) {
  const identity = useMemo(() => sessionToken(), []);
  const auth = useMemo<RoomAuth>(() => ({ token: identity.token }), [identity.token]);
  const [exists, setExists] = useState<boolean | null>(null);
  useEffect(() => { roomInfo(roomId).then(info => setExists(info.exists)).catch(() => setExists(true)); }, [roomId]);
  const room = useRoom(roomId, auth, connect);
  const verification = useVerification(room.snapshot);
  const clock: ServerClock = useMemo(() => updateClock({ offset: 0 }, room.snapshot?.serverTime ?? Date.now()), [room.snapshot?.serverTime]);

  if (exists === false || room.refusal === 'room_not_found') {
    return <main className="simple-page"><h1>This game does not exist</h1><p>The link may be wrong, or the server was restarted.</p><a href="/">Create a new game</a></main>;
  }
  if (room.refusal) return <main className="simple-page"><h1>Connection refused</h1><p>{REFUSAL_MESSAGES[room.refusal] ?? room.refusal}</p><a href="/">Create a new game</a></main>;
  if (!room.snapshot) return <main className="connecting">Connecting…</main>;
  return (
    <>
      {room.status === 'reconnecting' && <div className="banner warn">Reconnecting…</div>}
      {!identity.persistent && <div className="banner warn">Private browsing: refreshing this page will not resume your seat.</div>}
      <TableView snapshot={room.snapshot} history={room.history} verification={verification} clock={clock} act={room.act} roomId={roomId} />
      <Toasts errors={room.errors} dismiss={room.dismiss} />
    </>
  );
}
